import { Injectable, inject } from '@angular/core';
import { CanActivate, RouterStateSnapshot } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { NGXLogger } from 'ngx-logger';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { firstValueFrom } from 'rxjs';
import { AppConstants } from '../commons/app-constants';

// Google Play policy forbids embedding Razorpay/Cashfree checkout inside the Android app's own
// WebView for a digital subscription purchase. On Android only, this guard intercepts every
// real checkout entry point and hands the user off to a system browser (Chrome Custom Tab)
// instead, where the existing, unmodified web checkout runs unchanged. Web and iOS are
// untouched — canActivate returns true immediately for them.
//
// See TokenService.generateSubscribeHandoffToken/consumeSubscribeHandoffToken (tms-sb) for the
// short-lived, narrowly-scoped token this mints — deliberately NOT the app's normal login path,
// so the native app's own session is never affected.
@Injectable({ providedIn: 'root' })
export class AndroidPaymentHandoffGuard implements CanActivate {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(NGXLogger);

  private static readonly PENDING_FLAG = 'androidPaymentHandoffPending';

  async canActivate(_route: unknown, state: RouterStateSnapshot): Promise<boolean> {
    if (!(Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android')) {
      return true;
    }

    const returnPath = state.url.split('?')[0].split('#')[0];

    try {
      const response = await firstValueFrom(
        this.http.post<{ handoffToken: string }>(
          `${AppConstants.API_URL}api/v1/auth/mobile/subscribe-handoff-mint`,
          { returnPath }
        )
      );
      const handoffUrl = `${AppConstants.API_URL}api/v1/auth/mobile/subscribe-handoff-redeem?token=${encodeURIComponent(response.handoffToken)}`;
      localStorage.setItem(AndroidPaymentHandoffGuard.PENDING_FLAG, '1');
      await Browser.open({ url: handoffUrl });
    } catch (err) {
      this.logger.error('[AndroidPaymentHandoff] Failed to start browser checkout handoff', err);
      // Fail closed on the in-app route (never fall back to the embedded checkout — that's
      // exactly what this guard exists to prevent) but don't leave the user stranded silently.
    }

    return false;
  }
}
